/**
 * Imports
 */
import Axios from 'axios';
import { type AxiosResponse } from 'axios';
import { AuthStore } from '../stores/auth';
import { createI18n } from 'vue-i18n';
import { errorToast } from './ToastService';
import messages from '../i18n';
import router from '../router';
const i18n = createI18n({
  locale: 'en',
  legacy: false,
  messages,
  globalInjection: true,
});
const { t } = i18n.global;

export interface ISearchParams {
  query?: any;
  project?: any;
  limit?: number;
  page?: number;
  sort?: any;
}

export const axios = Axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  responseType: 'json',
  timeout: 30000,
  withCredentials: true, // This ensures the session cookie is sent with each request
});

const cancelTokenSource = Axios.CancelToken.source();

axios.interceptors.request.use((config) => {
  const authStore = AuthStore();
  authStore.setIsProcessing(true);

  // Grab the csrf token from the cookie (Django API)
  const csrftoken = getCookie('csrftoken');
  if (csrftoken) {
    config.headers['X-CSRFToken'] = csrftoken;
  }

  // Inject JWT bearer token if present (NestJS API)
  const accessToken = authStore.getAccessToken;
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Append a trailing  slash if it's not present in the url
  config.url = addTrailingSlash(config.url);

  // Only set these headers if the target is the UA API
  if (config.url?.includes(import.meta.env.VITE_API_URL as string) || !config.url?.match(/^http.*/)) {
    try {
      config.cancelToken = cancelTokenSource.token;

      // console.log('Logged in? ', authStore.getIsLoggedIn);
      // console.log('Request Config:', config);
    } catch (e: any) {
      console.log(e.message as string);
      authStore.logout();
    }
  }

  return config;
});

axios.interceptors.response.use(
  // Normal response scenario
  function responseMiddle(response) {
    // Don't do anything with this
    const authStore = AuthStore();
    authStore.setIsProcessing(false);
    return response;
  },
  async function onError(e) {
    try {
      const authStore = AuthStore();
      authStore.setIsProcessing(false);

      // Network error or API down
      if (!e.response) {
        // errorToast(t('errors.networkError'));
        throw new Error('errors.networkError');
      }

      // Safely extract error message with fallbacks
      const getErrorMessage = (response: any) => {
        if (!response || !response.data) {
          return 'errors.unknownError';
        }
        return response.data.message || 'errors.unknownError';
      };

      switch (e.response.status) {
        case 400: {
          // Bad request
          const message = getErrorMessage(e.response);
          if (!Array.isArray(message)) {
            throw new Error(message);
          }
          throw new Error(message.join(' '));
        }

        case 401: {
          // Unauthorized
          // Don't show additional error toast here since we're already showing session expired
          // and we don't want to trigger multiple API calls during logout
          if (!authStore.getIsLoggedIn) {
            // User is already logged out, just redirect
            router.push({
              name: 'login-view',
              query: { reason: 'session-expired' },
            });
          } else {
            console.error(t('errors.sessionExpired'));
            authStore.logout();
          }
          return Promise.reject(new Error('errors.sessionExpired'));
        }

        case 403: {
          // Forbidden
          const forbiddenMessage = getErrorMessage(e.response);
          throw new Error(forbiddenMessage);
        }

        case 404: {
          // Not found
          const notFoundMessage = getErrorMessage(e.response);
          throw new Error(notFoundMessage);
        }

        case 410: {
          // Gone
          const goneMessage = getErrorMessage(e.response);
          throw new Error(goneMessage);
        }

        default: {
          const defaultMessage = getErrorMessage(e.response);
          throw new Error(defaultMessage);
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'errors.unknownError';
      // errorToast(t(errorMessage));
      return Promise.reject(new Error(errorMessage));
    }
  },
);

function getCookie(name: string): string | null {
  if (!document.cookie) return null;

  const cookieVal = new Map(
    document.cookie.split(';').map(c => c.trim())
      .map(c => {
        const [key, ...v] = c.split('=');
        return [key, v.join('=')];
      }),
  ).get(name);

  if (!cookieVal) return null;

  return decodeURIComponent(cookieVal);
}

function addTrailingSlash(url: string | undefined): string | undefined {
  // Return undefined if url is null or undefined
  if (!url) {
    return url;
  }

  // Don't add slash if URL has query parameters
  if (url.includes('?')) {
    return url;
  }
  // Don't add slash if URL already ends with one
  if (url.endsWith('/')) {
    return url;
  }

  return url + '/';
}

export default {
  async get(path: string, opts: Record<string, any> = {}): Promise<AxiosResponse<any, any>> {
    return axios.get(path, opts);
  },
  async post(path: string, data: Record<string, string | number | object>, opts: Record<string, any> = {}): Promise<AxiosResponse<any, any>> {
    return axios.post(path, data, opts);
  },
  async put(path: string, data: Record<string, string | number | object>, opts: Record<string, any> = {}): Promise<AxiosResponse<any, any>> {
    return axios.put(path, data, opts);
  },
  async patch(path: string, data: Record<string, string | number | object>, opts: Record<string, any> = {}): Promise<AxiosResponse<any, any>> {
    return axios.patch(path, data, opts);
  },
  async delete(path: string, opts: Record<string, any> = {}): Promise<AxiosResponse<any, any>> {
    return axios.delete(path, opts);
  },
};
